
import {format, isToday, isValid, isYesterday, parse, parseISO} from 'date-fns'

import {useEffect} from 'react'

import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@App/hooks'
import { configMarkets } from '@App/redux/slices/config/config.selectors'
import { addFavoriteMarket } from '@App/redux/slices/config/ConfigSlice'
import { addSuccessNotify } from '@App/redux/slices/notifications/NotificationSlice'
import { ROUTE_NAMES } from '@App/ui/crypto_exchange/desktop/routes/routeNames'
import { fromZonedTime } from 'date-fns-tz'
import {
  asyncGetTransactions,
  asyncGetOrderTransactions,
  asyncGetTradeTransactions,
  DateTimeJson
} from "@Redux/slices/reports/ReportsSlice";

import {currentWallet} from '@Redux/slices/user/selectors'

export enum ReportsTabs {
  Transactions = 'transactions',
  Trades = 'trades',
  Orders = 'orders',
  Analytics = 'analytics',
}

export enum ReportsFilters {
  Type = 'type',
  Status = 'status',
  Period = 'period',
}

export type ReportsFilter = {
  [ReportsFilters.Period]: string
  [ReportsFilters.Status]: string[]
  [ReportsFilters.Type]: string[]
}

export type ReportTab = {
  name: string
  value: ReportsTabs
  filters: ReportsFilters[]
  showSendReport?: boolean
}

export const REPORTS_TABS: ReportTab[] = [
  {
    name: 'Transactions',
    value: ReportsTabs.Transactions,
    filters: [ReportsFilters.Type, ReportsFilters.Status, ReportsFilters.Period],
    showSendReport: true,
  },
  {
    name: 'Trades',
    value: ReportsTabs.Trades,
    filters: [ReportsFilters.Status, ReportsFilters.Period],
    showSendReport: true,
  },
  {
    name: 'Orders',
    value: ReportsTabs.Orders,
    filters: [ReportsFilters.Status, ReportsFilters.Period],
    showSendReport: true,
  },
  { name: 'Analytics', value: ReportsTabs.Analytics, filters: [ReportsFilters.Period] },
]

export const TIME_PERIOD_OPTIONS = [
  { name: '6 Hours', value: '6h' },
  { name: '12 Hours', value: '12h' },
  { name: '24 Hours', value: '24h' },
  { name: '3 Days', value: '3d' },
  { name: '1 Week', value: '1w' },
  { name: '1 Month', value: '1m' },
  { name: '2 Months', value: '2m' },
  { name: '3 Months', value: '3m' },
  { name: 'All', value: 'all' },
]

export const REPORT_TYPE_OPTIONS = [
  { name: 'Trade closed', value: 'trade_closed', separated: true },
  { name: 'Buy KO option', value: 'buy_ko_option' },
  { name: 'Sell KO option', value: 'sell_ko_option' },
  { name: 'Commissions', value: 'commissions' },
  { name: 'Overnight funding adjustment', value: 'overnight_funding_adjustment' },
  { name: 'Corrections', value: 'corrections' },
  { name: 'Bonus', value: 'bonus' },
  { name: 'Set balance', value: 'set_balance' },
]
export const REPORT_STATUS_OPTIONS = [
  { name: 'All', value: 'all', separated: true },
  { name: 'In review', value: 'in_review' },
  { name: 'Processing', value: 'processing' },
  { name: 'Processed', value: 'processed' },
  { name: 'Declined', value: 'declined' },
  { name: 'Cancelled', value: 'cancelled' },
  { name: 'Blocked', value: 'blocked' },
  { name: 'Refund', value: 'refund' },
  { name: 'Transaction void', value: 'transaction_void' },
  { name: 'Waiting for settlement', value: 'waiting_for_settlement' },
]

type DateInput = string | number | Date | DateTimeJson
const isDateTimeJson = (v: unknown): v is DateTimeJson =>
  typeof v === 'object' &&
  v !== null &&
  'date' in v &&
  'timezone' in v

export const normalizeDateString = (input: DateInput): string => {
  let date: Date

  switch (true) {
    case input instanceof Date:
      date = input as Date
      break
    case typeof input === 'number':
      date = new Date(input < 1e12 ? input as number * 1000 : input as number)
      break
    case isDateTimeJson(input): {
      // "2025-12-16 12:21:59.000000" + "Europe/Berlin"
      const withoutMicros = (input as DateTimeJson).date.split('.')[0]
      date = fromZonedTime(
        withoutMicros,
        (input as DateTimeJson).timezone
      )
      break
    }
    case typeof input === 'string' && input.includes('T'):
      date = parseISO(input as string)
      break
    case typeof input === 'string':
      date = parse(input as string, 'yyyy-MM-dd HH:mm:ss', new Date())
      break
    default:
      return ''
  }

  if (!isValid(date)) {
    return ''
  }

  return format(date, 'yyyy-MM-dd HH:mm:ss')
}

export const formatDateFromApi = (input: DateInput): string => formatDate(normalizeDateString(input))
export const formatTimeFromApi = (input: DateInput): string => formatTime(normalizeDateString(input))

export const formatDate = (dateString: string): string => {
  const date = parseISO(dateString);

  if (isToday(date)) {
    return 'Today'
  }

  if (isYesterday(date)) {
    return 'Yesterday'
  }

  return format(date, 'dd MMM yyyy')
}

export const formatTime = (dateString: string): string => {
  const date = parseISO(dateString);
  return format(date, 'HH:mm')
}

export function getTimestampFromPeriod(value: string): number | null {
  if (value === 'all') return null;

  const now = new Date();
  const match = value.match(/^(\d+)([hwdmy])$/);

  if (!match) throw new Error(`Invalid time period: ${value}`);

  const amount = parseInt(match[1], 10);
  const unit = match[2];

  let timestamp: number;

  switch (unit) {
    case 'h':
      timestamp = Math.floor(now.getTime() / 1000) - amount * 3600;
      break;
    case 'd':
      timestamp = Math.floor(now.getTime() / 1000) - amount * 24 * 3600;
      break;
    case 'w':
      timestamp = Math.floor(now.getTime() / 1000) - amount * 7 * 24 * 3600;
      break;
    case 'm': {
      const date = new Date(now);
      date.setMonth(date.getMonth() - amount);
      timestamp = Math.floor(date.getTime() / 1000);
      break;
    }
    case 'y': {
      const date = new Date(now);
      date.setFullYear(date.getFullYear() - amount);
      timestamp = Math.floor(date.getTime() / 1000);
      break;
    }
    default:
      throw new Error(`Unknown unit: ${unit}`);
  }

  return timestamp;
}


export const useCopyText = () => {
  const dispatch = useAppDispatch()
  return {
    copyText: async (text: string) => {
      await navigator.clipboard.writeText(text)
      dispatch(addSuccessNotify({ message: 'Copied to clipboard' }))
    },
  }
}

export const useSeeMarket = (data?: { sourceCurrency: string; targetCurrency: string }) => {
  const dispatch = useAppDispatch()
  const market = useAppSelector((state) =>
    configMarkets(state).find(
      (m) => m.source_currency === data?.sourceCurrency && m.target_currency === data?.targetCurrency,
    ),
  )
  const markets = useAppSelector(configMarkets)
  const navigate = useNavigate()

  return {
    seeMarket: (params?: { sourceCurrency: string; targetCurrency: string }) => {
      if (market) {
        dispatch(addFavoriteMarket(market))
        navigate(ROUTE_NAMES.CHARTS)
        return
      }
      if (params) {
        const _market = markets.find(
          (m) => m.source_currency === params.sourceCurrency && m.target_currency === params.targetCurrency,
        )
        if (_market) {
          dispatch(addFavoriteMarket(_market))
          navigate(ROUTE_NAMES.CHARTS)
        }
      }
    },
  }
}
